import React, { useState, useEffect } from "react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { getBasicQuizQuestions } from "../../api";

const DEFAULT_QUESTIONS = [
  {
    question: "What is the full form of NSE?",
    options: [
      "National Stock Exchange",
      "New Stock Exchange",
      "National Securities Exchange",
      "New Securities Exchange",
    ],
    correct: 0,
  },
  {
    question: "What is the minimum investment required for SIP?",
    options: ["₹100", "₹500", "₹1000", "₹5000"],
    correct: 0,
  },
  {
    question: "What does IPO stand for?",
    options: [
      "Initial Public Offering",
      "Internal Public Offering",
      "Investment Public Option",
      "Initial Private Offering",
    ],
    correct: 0,
  },
  {
    question: "What is the maximum loss in buying stocks?",
    options: [
      "Unlimited",
      "100% of investment",
      "50% of investment",
      "25% of investment",
    ],
    correct: 1,
  },
  {
    question: "What is the lock-in period for ELSS mutual funds?",
    options: ["1 year", "2 years", "3 years", "5 years"],
    correct: 2,
  },
];

const Quiz = () => {
  const [questions, setQuestions] = useState(DEFAULT_QUESTIONS);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const stored = await getBasicQuizQuestions();

        if (!mounted || !Array.isArray(stored) || stored.length === 0) return;

        const cleaned = stored
          .map((q) => ({
            question: q.question || "",
            options: Array.isArray(q.options) ? q.options : [],
            correct: typeof q.correct === "number" ? q.correct : 0,
          }))
          .filter((q) => q.question && q.options.length >= 2);

        if (cleaned.length) setQuestions(cleaned);
      } catch {}
    })();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    setSelectedAnswer(null);

    setTimeout(() => {
      if (document.activeElement) {
        document.activeElement.blur();
      }
    }, 0);

    window.scrollTo(0, 0);
  }, [currentQuestion]);

  const handleAnswer = (index) => {
    if (selectedAnswer !== null) return;

    setSelectedAnswer(index);

    const q = questions[currentQuestion];
    const correctIndex = q.correct ?? 0;

    if (index === correctIndex) {
      setScore((prev) => prev + 1);
    }

    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion((prev) => prev + 1);
      } else {
        setShowResult(true);
      }
    }, 400);
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setScore(0);
    setShowResult(false);
    setSelectedAnswer(null);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-grow flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-3xl">
          {/* Title */}
          <div className="text-center mb-10">
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-3">
              Basic Quiz
            </h1>

            <p className="text-gray-600">
              Test your knowledge with basic stock market questions.
            </p>
          </div>

          {!showResult ? (
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-lg">
              {/* Progress */}
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span>
                    Question {currentQuestion + 1} / {questions.length}
                  </span>

                  <span className="text-primary-600 font-semibold">
                    Score: {score}
                  </span>
                </div>

                <div className="w-full bg-gray-200 h-2 rounded-full">
                  <div
                    className="bg-primary-600 h-2 rounded-full transition-all"
                    style={{
                      width:
                        ((currentQuestion + 1) / questions.length) * 100 + "%",
                    }}
                  />
                </div>
              </div>

              {/* Question */}
              <h2 className="text-lg md:text-2xl font-bold text-gray-900 mb-6">
                {questions[currentQuestion].question}
              </h2>

              {/* Options */}
              <div className="grid gap-3">
                {questions[currentQuestion].options.map((option, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleAnswer(index)}
                    className={`w-full text-left p-4 border rounded-lg transition font-medium
                      
                      focus:outline-none focus:ring-0 active:bg-gray-50
                      
                      ${
                        selectedAnswer === index
                          ? "bg-primary-100 border-primary-400"
                          : "bg-gray-50 border-gray-200"
                      }

                      ${selectedAnswer === null ? "" : ""}
                    `}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white p-8 rounded-xl shadow text-center">
              <h2 className="text-3xl font-bold mb-4">Quiz Completed 🎉</h2>

              <p className="text-xl mb-4">
                Your Score:
                <span className="font-bold text-primary-600">
                  {" "}
                  {score}
                </span> / {questions.length}
              </p>

              <p className="text-gray-600 mb-6">
                {score === questions.length
                  ? "Perfect! You are a stock market expert."
                  : score >= questions.length / 2
                    ? "Good job! Keep learning."
                    : "Keep practicing to improve."}
              </p>

              <button
                onClick={resetQuiz}
                className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold"
              >
                Take Quiz Again
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Quiz;
